import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, IconButton, ClickAwayListener } from '@material-ui/core'
import SydAutocomplete from '../../../../components/commonDesign/SydAutocomplete'
import { useSearchClients } from '../../../../api/clients'
import { DEFAULT_SEARCH_CLIENTS_PAGE_SIZE } from '../../../../constants'
import useDebouncedValue from '../../../../hooks/useDebounceValue'
import Icon from '../../../../components/atoms/Icon'
import FastAvatar from '../../../../components/molecules/FastAvatar'

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    position: 'relative'
  },
  focusButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 500,
    color: 'var(--dai-color-text)',
    backgroundColor: 'transparent',
    border: 'var(--dai-border1)',
    borderRadius: 'var(--border-radius-md)',
    cursor: 'pointer',
    transition: 'all 150ms ease',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)',
      borderColor: 'var(--color-neutral-400)'
    },
    '& svg': {
      fontSize: '16px'
    }
  },
  selectorWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  autocomplete: {
    minWidth: '240px'
  },
  closeButton: {
    padding: '4px',
    color: 'var(--dai-color-text-dim)',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    },
    '& svg': {
      fontSize: '18px'
    }
  },
  selectedClient: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-sm)',
    padding: 'var(--padding-xs) var(--padding-sm) var(--padding-xs) var(--padding-md)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--dai-color-text)',
    backgroundColor: 'var(--dai-color-surface2)',
    border: 'var(--dai-border-2)',
    borderRadius: 'var(--border-radius-md)',
    boxShadow: 'var(--shadow-sm)',
    '& .client-name': {
      maxWidth: '180px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  },
  clearButton: {
    padding: '2px',
    marginLeft: '2px',
    color: 'var(--color-primary-600)',
    '&:hover': {
      backgroundColor: 'var(--color-primary-100)'
    },
    '& svg': {
      fontSize: '16px'
    }
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '4px 8px',
    width: '100%'
  },
  optionText: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  optionLabel: {
    fontSize: '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  optionSubLabel: {
    fontSize: '12px',
    color: 'var(--color-neutral-600)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}))

/**
 * Client focus selector for AI assistant
 * Allows selecting a specific client to focus the conversation on
 *
 * @param {object} props
 * @param {object|null} props.value - Current focus: { type: 'client', subjectIdentifier, subjectLabel } or null
 * @param {function} props.onChange - Called with focus object or null
 */
function ClientFocusSelector ({ value, onChange, disabled, error, size = 'sm' }) {
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearchValue = useDebouncedValue(searchValue, 300)
  const autocompleteRef = useRef(null)

  // Build text search query for client name matching
  const searchQuery = useMemo(() => {
    const query = {
      includes: { avatars: true },
      take: DEFAULT_SEARCH_CLIENTS_PAGE_SIZE
    }
    if (debouncedSearchValue?.trim()) {
      query.textSearch = {
        longName: [{ op: 'contains', value: debouncedSearchValue }],
        shortName: [{ op: 'contains', value: debouncedSearchValue }]
      }
    }
    return query
  }, [debouncedSearchValue])

  const { data: clientsResponse, isLoading } = useSearchClients(searchQuery, { enabled: isOpen })

  // Build options (no "All Data" option)
  const options = useMemo(() => {
    const clients = clientsResponse?.data ?? []
    return clients.map(({ clientId, shortName, longName, avatarUrl, clientAbbreviation }) => ({
      value: clientId,
      label: shortName,
      subLabel: longName !== shortName ? longName : undefined,
      avatarUrl,
      abbreviation: clientAbbreviation
    }))
  }, [clientsResponse])

  // Focus the input when opening
  useEffect(() => {
    if (isOpen && autocompleteRef.current) {
      const input = autocompleteRef.current.querySelector('input')
      if (input) {
        setTimeout(() => input.focus(), 50)
      }
    }
  }, [isOpen])

  const handleFocusClick = useCallback(() => {
    if (!disabled) {
      setIsOpen(true)
      setSearchValue('')
    }
  }, [disabled])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setSearchValue('')
  }, [])

  const handleClearSelection = useCallback(() => {
    onChange(null)
  }, [onChange])

  const handleChange = useCallback((e, selectedOption) => {
    if (selectedOption && selectedOption.value !== null) {
      onChange({
        type: 'client',
        subjectIdentifier: selectedOption.value,
        subjectLabel: selectedOption.label,
        avatarUrl: selectedOption.avatarUrl,
        abbreviation: selectedOption.abbreviation
      })
      setIsOpen(false)
      setSearchValue('')
    }
  }, [onChange])

  const handleInputChange = useCallback((e, inputValue) => {
    setSearchValue(inputValue)
  }, [])

  const renderOption = useCallback((option) => (
    <div className={classes.optionRow}>
      <FastAvatar
        avatarUrl={option.avatarUrl}
        abbreviation={option.abbreviation}
        size='xs'
      />
      <div className={classes.optionText}>
        <span className={classes.optionLabel}>{option.label}</span>
        {option.subLabel && (
          <span className={classes.optionSubLabel}>{option.subLabel}</span>
        )}
      </div>
    </div>
  ), [classes])

  const hasSelection = value?.subjectIdentifier != null

  // If a client is selected, show the selected state
  if (hasSelection && !isOpen) {
    return (
      <div className={classes.container}>
        <div className={classes.selectedClient}>
          <FastAvatar
            avatarUrl={value.avatarUrl}
            abbreviation={value.abbreviation}
            size='xs'
          />
          <span className='client-name'>{value.subjectLabel}</span>
          <IconButton
            className={classes.clearButton}
            onClick={handleClearSelection}
            disabled={disabled}
            size='small'
            aria-label='Clear client focus'
          >
            <Icon name='close' />
          </IconButton>
        </div>
      </div>
    )
  }

  // If selector is open, show the autocomplete
  if (isOpen) {
    return (
      <ClickAwayListener onClickAway={handleClose}>
        <div className={classes.container}>
          <div className={classes.selectorWrapper}>
            <div className={classes.autocomplete} ref={autocompleteRef}>
              <SydAutocomplete
                value={null}
                options={options}
                onChange={handleChange}
                onInputChange={handleInputChange}
                isLoading={isLoading}
                disabled={disabled}
                size={size}
                placeholder='Search clients...'
                autoFocus
                openOnFocus
                renderOption={renderOption}
              />
            </div>
            <IconButton
              className={classes.closeButton}
              onClick={handleClose}
              size='small'
              aria-label='Cancel'
            >
              <Icon name='close' />
            </IconButton>
          </div>
        </div>
      </ClickAwayListener>
    )
  }

  // Default state: show "Focus" button
  return (
    <div className={classes.container}>
      <button
        type='button'
        className={classes.focusButton}
        onClick={handleFocusClick}
        disabled={disabled}
        style={error ? { borderColor: 'var(--color-danger-500)', color: 'var(--color-danger-600)' } : undefined}
      >
        <Icon name='target' />
        Focus
      </button>
    </div>
  )
}

ClientFocusSelector.propTypes = {
  value: PropTypes.shape({
    type: PropTypes.string,
    subjectIdentifier: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    subjectLabel: PropTypes.string,
    avatarUrl: PropTypes.string,
    abbreviation: PropTypes.string
  }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  size: PropTypes.oneOf(['xs', 'sm', 'lg'])
}

ClientFocusSelector.defaultProps = {
  value: null,
  disabled: false,
  size: 'sm'
}

export default ClientFocusSelector
